/*
  Add Calendar.

  Two paths, picked with cards rather than buried in one dropdown (see
  CalendarMethodPicker):

    automatic - sign in at Google / Microsoft, no credentials typed anywhere.
    manual    - an Exchange server, with a version dropdown and the usual
                username / password / host.

  On the automatic path the sign-in also brings back every calendar on the
  account. Once the credential is saved, this modal steps aside and
  CalendarEntriesModal takes over: a separate, much wider modal whose only job
  is turning those entries into meeting rooms - Import creates them, Map points
  existing ones at a calendar.
*/

import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import { CircularProgress, Typography } from "@mui/material";
import { setCalendar } from "../redux/slices/calendarSlice";
import CalendarOAuthPanel from "./CalendarOAuthPanel";
import CalendarMethodPicker, {
  CONNECTION_METHODS,
} from "./CalendarMethodPicker";
import CalendarEntriesModal from "./CalendarEntriesModal";
import { fetchEmmsList } from "../utils/calendarProvisioning";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxWidth: 760,
  width: "95%",
  bgcolor: "background.paper",
  border: "2px solid #000",
  borderRadius: "10px",
  boxShadow: 24,
  paddingTop: 1,
  paddingBottom: 4,
  paddingLeft: 4,
  paddingRight: 4,
  overflowY: "auto",
  maxHeight: "90vh",
};

const useStyles = makeStyles((theme) => ({
  root: {
    "& .MuiTextField-root": {
      margin: theme.spacing(1),
      width: "30ch",
    },
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    position: "absolute",
    width: 300,
    backgroundColor: "#fff",
    boxShadow: "0 0 10px rgba(0, 0, 0, 0.5)",
    padding: 20,
    outline: "none",
    borderRadius: 5,
  },
  label: {
    fontWeight: "bold",
  },
  fieldContainer: {
    display: "flex",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: "1rem",
  },
}));

const EMPTY_FORM = {
  calendarType: "",
  credentialName: "",
  username: "",
  password: "",
  host: "",
  isImpersonateRoom: false,
  customProperties: {},
};

export default function MyCalendarForm({ selectedOption }) {
  const [open, setOpen] = React.useState(false);
  const headers = useSelector((state) => state.header);
  const classes = useStyles();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [error, setError] = useState(false);
  const curremms = useSelector((state) => state.curremms);
  const oldCalendarData = useSelector((state) => state.calendar);
  const dispatch = useDispatch();

  const [method, setMethod] = useState("");

  const [oauthResult, setOauthResult] = useState(null);

  const [savedCalendar, setSavedCalendar] = useState(null);
  const [entriesOpen, setEntriesOpen] = useState(false);

  const emmsheaders = useSelector((state) => state.emmsheader);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const isAutomatic = method === CONNECTION_METHODS.AUTOMATIC;
  const isManual = method === CONNECTION_METHODS.MANUAL;

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setMethod("");
    setOauthResult(null);
    setSavedCalendar(null);
    setEntriesOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  // The credential is already saved by the time the entries open.
  const handleEntriesClose = () => {
    setEntriesOpen(false);
    handleClose();
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleMethodChange = (nextMethod) => {
    setMethod(nextMethod);
    setOauthResult(null);
    setFormData((prevFormData) => ({
      ...EMPTY_FORM,
      credentialName: prevFormData.credentialName,
      customProperties: prevFormData.customProperties,
    }));
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setFormData((prevFormData) => ({ ...prevFormData, [name]: checked }));
  };

  const handleCalendarTypeChange = (calendarType) => {
    setOauthResult(null);
    setFormData((prevFormData) => ({
      ...prevFormData,
      calendarType,
    }));
  };

  const failWith = (message) => {
    setIsSubmitting(false);
    setErrorModalOpen(true);
    setErrorMessage(message);
    setError(true);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!method) {
      failWith("Choose how this calendar should be connected");
      return;
    }
    if (!formData.calendarType) {
      failWith(
        isAutomatic
          ? "Choose a provider to sign in with"
          : "Choose an Exchange version",
      );
      return;
    }
    if (!formData.credentialName) {
      failWith("Enter a credential name");
      return;
    }

    if (isAutomatic) {
      // EMMS saved the credential when the sign-in succeeded.
      if (!savedCalendar) {
        failWith(
          `Sign in with ${
            formData.calendarType === "Office 365" ? "Microsoft" : "Google"
          } to save this calendar`,
        );
        return;
      }
      setErrorModalOpen(true);
      setErrorMessage(`Calendar added successfully`);
      setError(false);
      return;
    } else if (!formData.username || !formData.password || !formData.host) {
      failWith("Enter all required fields");
      return;
    }

    setIsSubmitting(true);
    makeRequest();
  };

  const saveCredential = async () => {
    const requestBody = {
      path: "calendar",
      query: {},
      emmsId: `${selectedOption}`,
      body: { ...formData },
      requestType: "POST",
      headers: emmsheaders?.emmsheaders?.headers,
    };
    const api = localStorage.getItem("api");
    const response = await axios.post(
      api + `/emms/piggyback`,
      requestBody,
      headers,
    );

    const calendar = Array.isArray(response.data)
      ? response.data[0]
      : response.data;

    dispatch(setCalendar([...oldCalendarData.calendar, calendar]));
    return calendar;
  };

  const makeRequest = () => {
    saveCredential()
      .then(() => {
        setIsSubmitting(false);
        setErrorModalOpen(true);
        setErrorMessage(`Calendar added successfully`);
        setError(false);
        resetForm();
      })
      .catch((error) => {
        setIsSubmitting(false);
        setErrorModalOpen(true);
        setErrorMessage(
          `Error creating calendar: ${error?.response?.data || error?.message}`,
        );
        setError(true);
      });
  };

  const refreshCalendars = () =>
    fetchEmmsList({
      path: "calendar",
      emmsId: selectedOption,
      emmsHeaders: emmsheaders?.emmsheaders?.headers,
      requestConfig: headers,
    })
      .then((calendars) => dispatch(setCalendar(calendars)))
      .catch(() => {});

  // EMMS has saved the credential by now; only its id and the account's
  // calendars come back.
  const handleSignedIn = (outcome) => {
    setOauthResult(outcome || null);
    setSavedCalendar({
      id: outcome?.calendarCredentialId,
      credentialName: outcome?.credentialName || formData.credentialName,
    });
    refreshCalendars();

    if (outcome?.calendars?.length) {
      setOpen(false);
      setEntriesOpen(true);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleModalClose = () => {
    setErrorModalOpen(false);
    setErrorMessage("");
    setIsSubmitting(false);

    if (!error) {
      setOpen(false);
      resetForm();
    }
  };

  const getLabelWithAsterisk = (labelText, isRequired) => {
    return (
      <div style={{ display: "flex", alignItems: "center", width: "140px" }}>
        <label className={classes.label} htmlFor={labelText}>
          {labelText}
        </label>
        {isRequired && <span style={{ color: "red" }}>*</span>}
      </div>
    );
  };

  const errorDialog = (
    <Modal
      open={errorModalOpen}
      onClose={handleModalClose}
      className={classes.modal}
    >
      <div className={classes.paper}>
        <h2>{error === true ? "Oops" : "Congratulations"}</h2>
        <p>{errorMessage}</p>
        <Button
          style={{ textTransform: "none", marginLeft: "75%" }}
          onClick={handleModalClose}
          variant="contained"
          color="primary"
        >
          Close
        </Button>
      </div>
    </Modal>
  );

  return (
    <div>
      <Button
        style={{ textTransform: "none" }}
        variant="contained"
        color="primary"
        onClick={handleOpen}
      >
        Add Credentials
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={modalStyle}>
          <form
            className={classes.root}
            noValidate
            autoComplete="off"
            onSubmit={handleSubmit}
          >
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                  gap: "16px",
                  width: "100%",
                  margin: "20px 0 16px",
                }}
              >
                <h2 style={{ margin: 0, whiteSpace: "nowrap" }}>
                  Calendar Integration for EMMS
                </h2>
                <TextField
                  required
                  id="credentialName"
                  name="credentialName"
                  size="small"
                  label="Credential Name"
                  variant="standard"
                  value={formData.credentialName}
                  onChange={handleChange}
                  disabled={Boolean(savedCalendar)}
                  style={{ margin: 0, width: 240, flexShrink: 0 }}
                />
              </div>

              <div className="first-modal">
                <CalendarMethodPicker
                  method={method}
                  calendarType={formData.calendarType}
                  onMethodChange={handleMethodChange}
                  onCalendarTypeChange={handleCalendarTypeChange}
                  disabled={isSubmitting || Boolean(savedCalendar)}
                />

                {isAutomatic && formData.calendarType ? (
                  <>
                    <CalendarOAuthPanel
                      calendarType={formData.calendarType}
                      emmsId={selectedOption}
                      emmsHeaders={emmsheaders?.emmsheaders?.headers}
                      requestConfig={headers}
                      credentialName={formData.credentialName.trim()}
                      calendarId={savedCalendar?.id}
                      signedIn={Boolean(savedCalendar)}
                      onSignedIn={handleSignedIn}
                      disabled={!formData.credentialName.trim()}
                    />

                    {oauthResult?.calendars?.length ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                          mb: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "text.secondary" }}
                        >
                          <strong>{oauthResult.calendars.length}</strong>{" "}
                          calendar{" "}
                          {oauthResult.calendars.length === 1
                            ? "entry"
                            : "entries"}{" "}
                          fetched.
                        </Typography>
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: "none" }}
                          onClick={() => {
                            setOpen(false);
                            setEntriesOpen(true);
                          }}
                        >
                          View entries
                        </Button>
                      </Box>
                    ) : null}
                  </>
                ) : null}

                {isManual && formData.calendarType ? (
                  <div>
                    <div className={classes.fieldContainer}>
                      {getLabelWithAsterisk("Username:", true)}
                      <TextField
                        required
                        id="username"
                        name="username"
                        size="small"
                        label="eg John Doe"
                        variant="outlined"
                        value={formData.username}
                        onChange={handleChange}
                      />
                    </div>

                    <div className={classes.fieldContainer}>
                      {getLabelWithAsterisk("Password:", true)}
                      <TextField
                        required
                        id="password"
                        name="password"
                        size="small"
                        label="eg john@123"
                        variant="outlined"
                        value={formData.password}
                        onChange={handleChange}
                      />
                    </div>

                    <div className={classes.fieldContainer}>
                      {getLabelWithAsterisk("Host:", true)}
                      <TextField
                        required
                        id="host"
                        name="host"
                        label="https://X.X.X.X"
                        size="small"
                        variant="outlined"
                        value={formData.host}
                        onChange={handleChange}
                      />
                    </div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={Boolean(formData.isImpersonateRoom)}
                          onChange={handleCheckboxChange}
                          name="isImpersonateRoom"
                          color="primary"
                        />
                      }
                      label="Impersonate Meeting Room"
                    />
                  </div>
                ) : null}
              </div>

              <div className={classes.buttonContainer}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                  }}
                >
                  <Button
                    style={{ textTransform: "none" }}
                    variant="outlined"
                    color="error"
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    Close
                  </Button>
                  <Button
                    style={{
                      textTransform: "none",
                    }}
                    type="submit"
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? <CircularProgress size={24} /> : "Verify"}
                  </Button>
                </Box>
              </div>

              {errorDialog}
            </div>
          </form>
        </Box>
      </Modal>

      <CalendarEntriesModal
        open={entriesOpen}
        onClose={handleEntriesClose}
        calendarCredentialId={savedCalendar?.id}
        credentialName={
          savedCalendar?.credentialName || formData.credentialName
        }
        calendars={oauthResult?.calendars}
        emmsId={selectedOption}
        emmsHeaders={emmsheaders?.emmsheaders?.headers}
        requestConfig={headers}
      />
    </div>
  );
}
